import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { propsDeAtivacao } from './interativo';
import type { EventoDeAtivacao } from './interativo';

export interface CardProps {
    title?: ReactNode;
    subtitle?: ReactNode;
    actions?: ReactNode;
    footer?: ReactNode;
    /** Espaçamento interno em px. 16–24, pelo sistema de espaçamento. */
    padding?: number;
    /** Eleva no hover. Só para card clicável. */
    interactive?: boolean;
    /** Torna o card clicável: recebe role="button", tabIndex e Enter/Espaço. */
    onClick?: (event: EventoDeAtivacao) => void;
    /** Nome acessível do card clicável — o conteúdo visual raramente basta. */
    ariaLabel?: string;
    children?: ReactNode;
    style?: CSSProperties;
}

export function Card({
    title, subtitle, actions, footer, padding = 20, interactive = false, onClick, ariaLabel, children, style,
}: CardProps) {
    const [hover, setHover] = useState(false);
    const clicavel = Boolean(onClick);

    /* Só quando há `onClick`. Anexá-los sempre punha um manipulador num
       <section>, que é elemento não interativo — e o card ficava fora da ordem
       de tabulação para quem navega por teclado. */
    const interacao = propsDeAtivacao(onClick, ariaLabel);

    return (
        <section
            {...interacao}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                cursor: clicavel ? 'pointer' : undefined,
                textAlign: 'left',
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
                boxShadow: interactive && hover ? 'var(--shadow-sm)' : 'none',
                transition: 'box-shadow var(--dur-fast) var(--ease-standard), border-color var(--dur-fast) var(--ease-standard)',
                borderColor: interactive && hover ? 'var(--border-strong)' : 'var(--border)',
                overflow: 'hidden', ...style,
            }}
        >
            {(title || actions) && (
                /* Quebra quando falta largura: um seletor de 220 px ao lado do
                   título empurrava o card para fora da tela no celular. */
                <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px 16px', padding: `${padding}px ${padding}px 0` }}>
                    <div style={{ flex: '1 1 12rem', minWidth: 0 }}>
                        {title && (
                            <h3 style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 15, lineHeight: '22px', fontWeight: 600, letterSpacing: 'var(--tracking-snug)', color: 'var(--text-primary)' }}>
                                {title}
                            </h3>
                        )}
                        {subtitle && <p style={{ margin: '3px 0 0', fontSize: 13, lineHeight: '18px', color: 'var(--text-secondary)' }}>{subtitle}</p>}
                    </div>
                    {actions && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: '0 1 auto', minWidth: 0, maxWidth: '100%' }}>{actions}</div>}
                </header>
            )}
            <div style={{ padding: title || actions ? `14px ${padding}px ${padding}px` : padding }}>{children}</div>
            {footer && (
                <div style={{ borderTop: '1px solid var(--border-subtle)', padding: `12px ${padding}px`, background: 'var(--surface-secondary)' }}>
                    {footer}
                </div>
            )}
        </section>
    );
}
